import React, { useState } from 'react';
import { useSimCard, useSimStatus, useSimConnectivity } from '@/hooks/useSimCards';
import { ISimCard } from '@/types/api';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';

interface SimCardDetailProps {
  iccid: string;
  onBack?: () => void;
  onEdit?: (sim: ISimCard) => void;
}

const SimCardDetail: React.FC<SimCardDetailProps> = ({ 
  iccid, 
  onBack, 
  onEdit 
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'status' | 'connectivity'>('overview');
  
  const { data: sim, isLoading: simLoading, error: simError } = useSimCard(iccid);
  const { data: status, isLoading: statusLoading, error: statusError } = useSimStatus(iccid);
  const { data: connectivity, isLoading: connectivityLoading, error: connectivityError } = useSimConnectivity(iccid);

  if (simLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (simError) {
    return (
      <ErrorMessage 
        message={simError instanceof Error ? simError.message : 'Failed to load SIM details'}
        onRetry={() => window.location.reload()}
      />
    );
  }

  if (!sim) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500 dark:text-gray-400 text-lg">SIM card not found</div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Enabled':
        return 'bg-green-100 text-green-800';
      case 'Disabled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800 dark:text-gray-200';
    }
  };

  const getQuotaStatusColor = (quotaStatus?: { id: number; description: string }) => {
    if (!quotaStatus) return 'bg-gray-100 text-gray-800 dark:text-gray-200';
    
    switch (quotaStatus.description) {
      case 'OK':
        return 'bg-green-100 text-green-800';
      case 'Warning':
        return 'bg-yellow-100 text-yellow-800';
      case 'Critical':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800 dark:text-gray-200';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatDataUsage = (quota?: number) => {
    if (quota === undefined) return 'N/A';
    return `${(quota / 1024).toFixed(2)} GB`;
  };

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Basic Information */}
      <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
            Basic Information
          </h3>
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">ICCID</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white font-mono">{sim.iccid}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">ICCID with Luhn</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white font-mono">{sim.iccid_with_luhn || 'N/A'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">IMSI</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white font-mono">{sim.imsi || 'N/A'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">MSISDN</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white font-mono">{sim.msisdn || 'N/A'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">IMEI</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white font-mono">{sim.imei || 'N/A'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">IP Address</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white font-mono">{sim.ip_address || 'Not assigned'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Label</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white">{sim.label || 'No label'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Activation Date</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(sim.activation_date)}</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Status Information */}
      <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
            Status & Configuration
          </h3>
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</dt>
              <dd className="mt-1">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(sim.status)}`}>
                  {sim.status}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">IMEI Lock</dt>
              <dd className="mt-1">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  sim.imei_lock ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800 dark:text-gray-200'
                }`}>
                  {sim.imei_lock ? 'Enabled' : 'Disabled'}
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Data Quota</dt>
              <dd className="mt-1">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-900 dark:text-white">{formatDataUsage(sim.current_quota)}</span>
                  {sim.quota_status && (
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getQuotaStatusColor(sim.quota_status)}`}>
                      {sim.quota_status.description}
                    </span>
                  )}
                </div>
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">SMS Quota</dt>
              <dd className="mt-1">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-900 dark:text-white">{sim.current_quota_SMS || 'N/A'}</span>
                  {sim.quota_status_SMS && (
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getQuotaStatusColor(sim.quota_status_SMS)}`}>
                      {sim.quota_status_SMS.description}
                    </span>
                  )}
                </div>
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );

  const renderStatusTab = () => {
    if (statusLoading) {
      return (
        <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex flex-col items-center justify-center h-64 space-y-4">
              <LoadingSpinner size="large" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading status information from 1NCE API...</p>
              <p className="text-xs text-gray-400 dark:text-gray-500">This may take a few seconds</p>
            </div>
          </div>
        </div>
      );
    }

    if (statusError) {
      return (
        <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <ErrorMessage
              message={statusError instanceof Error ? statusError.message : 'Failed to load SIM status'}
            />
          </div>
        </div>
      );
    }

    if (!status) {
      return (
        <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">No status data available</div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Connection Status */}
        <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
              Connection Status
            </h3>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</dt>
                <dd className="mt-1">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    status.status === 'ONLINE' ? 'bg-green-100 text-green-800' :
                    status.status === 'OFFLINE' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {status.status}
                  </span>
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Services</dt>
                <dd className="mt-1">
                  <div className="flex flex-wrap gap-1">
                    {status.services && status.services.length > 0 ? (
                      status.services.map((service, index) => (
                        <span key={index} className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {service}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-500 dark:text-gray-400">No services</span>
                    )}
                  </div>
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Location Information */}
        {status.location && (
          <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
                Location Information
              </h3>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Updated</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(status.location.last_updated)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Last Updated GPRS</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(status.location.last_updated_gprs)}</dd>
                </div>
                {status.location.operator && (
                  <>
                    <div>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Operator</dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-white">{status.location.operator.name}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Country</dt>
                      <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                        {status.location.operator.country?.name} ({status.location.operator.country?.['iso-code']?.toUpperCase()})
                      </dd>
                    </div>
                  </>
                )}
              </dl>
            </div>
          </div>
        )}

        {/* PDP Context */}
        {status.pdp_context && (
          <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
                PDP Context
              </h3>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">UE IP Address</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white font-mono">{status.pdp_context.ue_ip_address}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Duration</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{status.pdp_context.duration}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">RAT Type</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{status.pdp_context.rat_type.description}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Created</dt>
                  <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(status.pdp_context.created)}</dd>
                </div>
              </dl>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderConnectivityTab = () => {
    if (connectivityLoading) {
      return (
        <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex flex-col items-center justify-center h-64 space-y-4">
              <LoadingSpinner size="large" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading connectivity information from 1NCE API...</p>
              <p className="text-xs text-gray-400 dark:text-gray-500">This may take a few seconds</p>
            </div>
          </div>
        </div>
      );
    }

    if (connectivityError) {
      return (
        <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <ErrorMessage
              message={connectivityError instanceof Error ? connectivityError.message : 'Failed to load connectivity data'}
            />
          </div>
        </div>
      );
    }

    if (!connectivity) {
      return (
        <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">No connectivity data available</div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
              Connectivity Information
            </h3>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Request Timestamp</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(connectivity.request_timestamp)}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Reply Timestamp</dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white">{formatDate(connectivity.reply_timestamp)}</dd>
              </div>
              {connectivity.subscriber_info && (
                <>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Location Retrieved</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white">
                      {connectivity.subscriber_info.current_location_retrieved ? 'Yes' : 'No'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Location Age</dt>
                    <dd className="mt-1 text-sm text-gray-900 dark:text-white">{connectivity.subscriber_info.age_of_location} minutes</dd>
                  </div>
                </>
              )}
            </dl>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-4">
          {onBack && (
            <button
              onClick={onBack}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              SIM Details
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 font-mono">{sim.iccid}</p>
          </div>
        </div>
        
        {onEdit && (
          <button
            onClick={() => onEdit(sim)}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Edit SIM
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('overview')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'overview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:border-gray-600'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('status')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'status'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:border-gray-600'
            }`}
          >
            Status
          </button>
          <button
            onClick={() => setActiveTab('connectivity')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'connectivity'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:border-gray-600'
            }`}
          >
            Connectivity
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-96">
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'status' && renderStatusTab()}
        {activeTab === 'connectivity' && renderConnectivityTab()}
      </div>
    </div>
  );
};

export default SimCardDetail;